import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useClientServices, ClientService } from '../../client-services/useData';

export interface ServicesExpiring30DaysProps {
  limit?: number;
  embedded?: boolean;
}

const ServicesExpiring30Days: React.FC<ServicesExpiring30DaysProps> = ({ limit = 5, embedded }) => {
  const navigate = useNavigate();
  const { services, isLoading } = useClientServices();

  const expiringServices = React.useMemo(() => {
    const now = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(now.getDate() + 30);
    
    return (services || [])
      .filter((service: ClientService) => {
        if (!service.serviceEndDate || !service.serviceActive) return false;
        const endDate = new Date(service.serviceEndDate);
        // Check if end date is between now and 30 days from now
        return endDate >= now && endDate <= thirtyDaysFromNow;
      })
      .sort((a: ClientService, b: ClientService) => {
        const dateA = new Date(a.serviceEndDate || 0).getTime();
        const dateB = new Date(b.serviceEndDate || 0).getTime();
        return dateA - dateB;
      })
      .slice(0, limit);
  }, [services, limit]);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getDaysUntilExpiry = (dateString?: string): number => {
    if (!dateString) return 0;
    const now = new Date();
    const endDate = new Date(dateString);
    const diffTime = endDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const cardClass = embedded ? 'min-w-0 flex-1 flex flex-col min-h-0' : 'bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 border border-gray-200/80 dark:border-gray-600/50';
  const listClass = embedded ? 'flex-1 min-h-0 overflow-y-auto space-y-1' : 'space-y-1 max-h-64 overflow-y-auto';
  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3 flex-shrink-0">Services Expiring (30 Days)</h2>
        <div className={listClass}>
          {[1, 2, 3].map((item) => (
            <div key={item} className="flex items-center space-x-2 sm:space-x-3 pb-2 border-b border-gray-100 last:border-0">
              <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-gray-200 animate-pulse flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <div className="h-3 bg-gray-200 animate-pulse rounded w-3/4 mb-1 sm:mb-1.5"></div>
                <div className="h-2 sm:h-2.5 bg-gray-200 animate-pulse rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Services Expiring (30 Days)</h2>
        <button
          onClick={() => navigate('/client-services')}
          className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium whitespace-nowrap transition-colors"
        >
          View All
        </button>
      </div>
      <div className={listClass}>
        {expiringServices.length > 0 ? (
          expiringServices.map((service: ClientService) => {
            const daysUntilExpiry = getDaysUntilExpiry(service.serviceEndDate);
            const isUrgent = daysUntilExpiry <= 7;
            
            return (
              <div
                key={service.clientServicetId}
                onClick={() => navigate(`/client-services/${service.clientServicetId}`)}
                className="flex items-center space-x-2 sm:space-x-3 p-1.5 sm:p-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border-b border-gray-100 last:border-0"
              >
                <div className="flex-shrink-0">
                  <div className={`h-7 w-7 sm:h-8 sm:w-8 rounded-full flex items-center justify-center text-white text-[10px] sm:text-xs font-semibold ${
                    isUrgent 
                      ? 'bg-gradient-to-br from-red-500 to-red-600' 
                      : 'bg-gradient-to-br from-orange-500 to-orange-600'
                  }`}>
                    {daysUntilExpiry}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] sm:text-xs font-medium text-gray-900 truncate">
                    {service.client?.clientName || `Client #${service.clientId}`}
                  </p>
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 flex-wrap">
                    <span className="text-[10px] sm:text-xs text-gray-500 truncate">
                      {service.serviceCategoryDescr?.psc_descr || service.serviceTypeDescr?.pst_descr || 'Service'}
                    </span>
                    <span className="text-[10px] sm:text-xs text-gray-400 hidden sm:inline">•</span>
                    <span className={`text-[10px] sm:text-xs font-medium ${
                      isUrgent ? 'text-red-600' : 'text-orange-600'
                    }`}>
                      {formatDate(service.serviceEndDate)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-4 sm:py-6 text-gray-500">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-xs sm:text-sm">No services expiring in the next 30 days</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesExpiring30Days;

